// Display adapter only. Discount rules remain owned by the installed CPD app.
export function hasCpdPriceEvidence(input, cpd) {
  if (cpd?.settings?.enabled !== true) return false;
  const product = cpd.products?.[input.handle];
  const variant = product?.[input.variantId];
  const rate = Number(cpd.currency?.rate);
  if (!variant || !Number.isFinite(variant.price) || variant.price < 0 ||
      !Number.isFinite(rate) || rate <= 0 || cpd.currency?.active !== input.currency) return false;
  let packPrice = variant.price * 100 * rate;
  if (input.surface === 'product' || input.surface === 'card') {
    packPrice = Number((packPrice / 100).toFixed(2)) * 100;
  } else if (typeof cpd.pricing?.resolveCartLinePrices === 'function') {
    try {
      const prices = cpd.pricing.resolveCartLinePrices(
        { price: input.packPrice, line_price: input.linePrice }, variant, input.quantity, product
      );
      packPrice = prices?.productPrice * rate;
    } catch { return false; }
  }
  // The installed app applies price records independently of customer.has_discount.
  // Compare numeric prices in the same currency, rather than treating that flag as proof.
  return Number.isFinite(packPrice) && packPrice >= 0 &&
    Math.abs(packPrice - input.packPrice) > 0.005;
}

export function resolvePackPrice(input, cpd) {
  const { units, quantity, currency } = input;
  if (!Number.isFinite(units) || units <= 0 || !Number.isInteger(quantity) || quantity < 1) {
    return { state: 'invalid' };
  }
  const pending = { state: 'pending' };
  const appPriceEvidence = hasCpdPriceEvidence(input, cpd);
  // Do not substitute retail prices for an unidentified logged-in customer.
  if (input.customerPresent && cpd?.settings?.enabled !== false &&
      (cpd?.settings?.enabled !== true || typeof cpd.customer?.has_discount !== 'boolean') &&
      !appPriceEvidence) return pending;
  const enabled = cpd?.settings?.enabled === true;
  if (enabled && typeof cpd.customer?.has_discount !== 'boolean' && !appPriceEvidence) return pending;
  if (enabled && (cpd.customer?.has_discount === true || appPriceEvidence)) {
    if (input.sellingPlan) return pending; // CPD's inspected resolver has no subscription input.
    const product = cpd.products?.[input.handle];
    const variant = product?.[input.variantId];
    const resolve = cpd.pricing?.resolveCartLinePrices;
    const rate = Number(cpd.currency?.rate);
    if (!variant || !Number.isFinite(variant.price) || variant.price < 0 ||
        !Number.isFinite(rate) || rate <= 0 ||
        cpd.currency?.active !== currency) return pending;
    if (input.surface === 'product' || input.surface === 'card') {
      // The installed PDP adapter displays variant.price, not the cart's tier resolver.
      // Round exactly as its two-decimal money formatter before deriving a piece price.
      const packPrice = Number((variant.price * 100 * rate / 100).toFixed(2)) * 100;
      return { state: 'ready', source: 'cpd', packPrice, linePrice: packPrice * quantity,
        piecePrice: packPrice / units / 100 };
    }
    if (typeof resolve !== 'function') return pending;
    let prices;
    try {
      prices = resolve({ price: input.packPrice, line_price: input.linePrice }, variant, quantity, product);
    } catch {
      return pending;
    }
    // CPD returns cents in its source currency; its display applies the rate once.
    const packPrice = prices?.productPrice * rate;
    const linePrice = prices?.lineItemPrice * rate;
    if (![packPrice, linePrice].every(value => Number.isFinite(value) && value >= 0)) return pending;
    return { state: 'ready', source: 'cpd', packPrice, linePrice,
      compareAtPrice: prices.productCompareAtPrice * rate,
      piecePrice: linePrice / quantity / units / 100 };
  }
  let packPrice = input.packPrice;
  let linePrice = input.linePrice;
  let quantityBreak = false;
  if (input.surface === 'product') linePrice = packPrice * quantity;
  if (input.surface === 'quick-buy') {
    const applicable = (input.quantityBreaks || []).filter(tier =>
      Number.isInteger(tier.minimum) && tier.minimum > 0 && tier.minimum <= quantity &&
      Number.isFinite(tier.price) && tier.price >= 0
    ).sort((a, b) => b.minimum - a.minimum)[0];
    if (applicable) {
      packPrice = applicable.price;
      quantityBreak = true;
    }
    linePrice = packPrice * quantity;
  }
  if (![packPrice, linePrice].every(value => Number.isFinite(value) && value >= 0)) return pending;
  return { state: 'ready', source: 'shopify', packPrice, linePrice, quantityBreak,
    piecePrice: linePrice / quantity / units / 100 };
}

export function formatPiecePrice(value, locale, currency) {
  return new Intl.NumberFormat(locale, {
    style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 3
  }).format(value);
}

if (typeof window !== 'undefined' && !customElements.get('bp-pack-content')) {
  class PackContent extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      const refresh = () => this.scheduleUpdate();
      this.confirmedAddedLine = this.dataset.surface !== 'added';
      const updateAddedLine = event => {
        if (this.dataset.surface !== 'added') return;
        const cart = event.detail?.cart;
        if (!Array.isArray(cart?.items)) return;
        if (event.type === 'variant:add') {
          const added = (event.detail.items || []).filter(item =>
            String(item.variant_id ?? item.id) === this.dataset.variantId);
          this.addedKey = added.length === 1 ? added[0].key : null;
        }
        const candidates = cart.items.filter(item =>
          String(item.variant_id ?? item.id) === this.dataset.variantId);
        const line = this.addedKey ? candidates.find(item => item.key === this.addedKey) :
          (candidates.length === 1 ? candidates[0] : null);
        this.confirmedAddedLine = Boolean(line);
        if (line) {
          this.dataset.lineKey = line.key;
          this.dataset.quantity = line.quantity;
          this.dataset.linePrice = line.final_line_price;
          this.dataset.originalLinePrice = line.original_line_price;
          this.dataset.sellingPlan = line.selling_plan_allocation ? 'true' : 'false';
        }
        refresh();
      };
      window.addEventListener('variant:add', updateAddedLine, { signal });
      document.addEventListener('cart:change', updateAddedLine, { signal });
      for (const name of ['cpd_prices_ready', 'cart_changed', 'load']) {
        window.addEventListener(name, refresh, { signal });
      }
      for (const name of ['cart:change', 'cart:refresh']) {
        document.addEventListener(name, refresh, { signal });
      }
      this.form = document.getElementById(this.dataset.formId);
      this.ownsHeader = false;
      this.confirmedPiecePrice = null;
      this.form?.addEventListener('change', refresh, { signal });
      this.form?.addEventListener('variant:change', refresh, { signal });
      // Also catches form-associated quantity controls outside the <form>.
      document.addEventListener('change', event => {
        if (this.form && event.target.form === this.form) refresh();
      }, { signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) refresh();
      });
      this.priceContainer = this.dataset.surface === 'card'
        ? this.closest('product-card')?.querySelector('.custom_card_price')
        : this.parentElement;
      if (this.priceContainer) {
        this.observer.observe(this.priceContainer, { childList: true, subtree: true });
      }
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }

    input() {
      const data = this.dataset;
      let quantityBreaks = [];
      try { quantityBreaks = JSON.parse(data.quantityBreaks || '[]'); } catch { /* No native tiers. */ }
      const quantityControl = this.form?.elements.namedItem('quantity');
      return {
        surface: data.surface, handle: data.handle, variantId: data.variantId,
        units: Number(data.units), quantity: Number(quantityControl?.value || data.quantity),
        packPrice: Number(data.packPrice), linePrice: Number(data.linePrice),
        currency: data.currency, customerPresent: data.customerPresent === 'true',
        sellingPlan: data.sellingPlan === 'true', quantityBreaks
      };
    }

    moneyHtml(cents, cpd) {
      // index.js's formatter does NOT apply currency.rate; the resolver above already did.
      const formatter = cpd?.shopify?.formatMoney;
      const format = cpd?.shop?.money_format;
      if (typeof formatter !== 'function' || !format) return null;
      try { return formatter(cents, format); } catch { return null; }
    }

    plainMoney(html) {
      const template = document.createElement('template');
      template.innerHTML = html;
      return template.content.textContent.replace(/\s/g, '');
    }

    priceText(sale) {
      const copy = sale.cloneNode(true);
      copy.querySelectorAll('.sr-only, .visually-hidden').forEach(node => node.remove());
      return copy.textContent.replace(/\s/g, '');
    }

    update() {
      if (!this.isConnected) return;
      const output = this.querySelector('[data-pack-unit-price]');
      const input = this.input();
      const locale = document.documentElement.lang || 'de';
      // Liquid renders the packaging amount once; variant changes replace this element.
      let result = resolvePackPrice(input, window.CPD);
      if (!this.confirmedAddedLine) result = { state: 'pending' };
      const activeVariant = this.form?.elements.namedItem('id')?.value;
      // The selected form variant may change before Impact replaces the quick-buy HTML.
      if (activeVariant && activeVariant !== input.variantId) result = { state: 'pending' };
      const priceList = this.priceContainer?.querySelector('price-list');
      const sale = priceList?.querySelector('sale-price');
      const compare = priceList?.querySelector('compare-at-price');
      if (compare && !this.originalCompare) {
        this.originalCompare = { html: compare.innerHTML, hidden: compare.hidden };
      }
      if (result.state === 'ready' && result.source === 'cpd') {
        const expected = this.moneyHtml(
          ['cart', 'added'].includes(input.surface) ? result.linePrice : result.packPrice, window.CPD
        );
        if (!sale || expected === null) result = { state: 'pending' };
        else if (['quick-buy', 'added'].includes(input.surface)) {
          // CPD has no selector for these quick-buy/confirmation headers. Use its
          // own result for both the displayed price and the derived piece price.
          if (this.priceText(sale) !== this.plainMoney(expected)) sale.innerHTML = expected;
          this.ownsHeader = true;
          if (compare) {
            compare.hidden = window.CPD.settings.settings?.hideCompareAtPrice === true ||
              !(result.compareAtPrice > result.packPrice);
            if (!compare.hidden) {
              const text = this.moneyHtml(result.compareAtPrice *
                (input.surface === 'added' ? input.quantity : 1), window.CPD);
              if (text !== null && compare.innerHTML !== text) compare.innerHTML = text;
            }
          }
        } else if (input.surface === 'card'
            ? !this.priceText(sale).endsWith(this.plainMoney(expected))
            : this.priceText(sale) !== this.plainMoney(expected)) {
          // Wait until CPD has also updated the cart's visible line price.
          result = { state: 'pending' };
        }
      } else if (result.state === 'ready' && input.surface === 'product' &&
          window.CPD?.settings?.enabled === true) {
        const expected = this.moneyHtml(result.packPrice, window.CPD);
        if (!sale || expected === null || this.priceText(sale) !== this.plainMoney(expected)) {
          // Even a retail fallback must match the visible PDP price. Never leave an
          // old retail-derived piece price beside a discounted packaging price.
          result = { state: 'pending' };
        }
      } else if (result.state === 'ready' && (input.surface === 'added' ||
          (input.surface === 'quick-buy' && (result.quantityBreak || this.ownsHeader)))) {
        if (!sale) result = { state: 'pending' };
        else {
          const text = new Intl.NumberFormat(locale, {
            style: 'currency', currency: input.currency
          }).format((input.surface === 'added' ? result.linePrice : result.packPrice) / 100);
          if (this.priceText(sale) !== text.replace(/\s/g, '')) sale.textContent = text;
          this.ownsHeader = true;
          if (compare && input.surface === 'added') {
            const original = Number(this.dataset.originalLinePrice);
            compare.hidden = !Number.isFinite(original) || original <= result.linePrice;
            if (!compare.hidden) {
              const text = new Intl.NumberFormat(locale, {
                style: 'currency', currency: input.currency
              }).format(original / 100);
              if (this.priceText(compare) !== text.replace(/\s/g, '')) compare.textContent = text;
            }
          } else if (compare && this.originalCompare) {
            compare.hidden = this.originalCompare.hidden;
            if (compare.innerHTML !== this.originalCompare.html) compare.innerHTML = this.originalCompare.html;
          }
        }
      }
      // A collection card can show an "from" prefix. Its amount must still
      // match the reference variant; never parse a formatted price for maths.
      if (result.state === 'ready' && input.surface === 'card' && result.source === 'shopify') {
        const reference = (this.dataset.referencePrice || '').replace(/\s/g, '');
        if (!sale || !reference || !this.priceText(sale).endsWith(reference)) {
          result = { state: 'pending' };
        }
      }
      if (!output) return;
      const ready = result.state === 'ready' && Boolean(this.dataset.unitLabel?.trim());
      // A temporary app state must not clear a price that still matches the
      // visible amount and the same variant, currency, content and quantity.
      const visiblePrice = sale ? this.priceText(sale) : '';
      const priceKey = visiblePrice && this.confirmedAddedLine &&
        (!activeVariant || activeVariant === input.variantId)
        ? JSON.stringify([
          input.surface, input.handle, input.variantId, input.units,
          this.dataset.unitLabel, input.quantity, input.currency, locale,
          input.customerPresent, input.sellingPlan, input.packPrice,
          input.linePrice, this.dataset.lineKey, visiblePrice
        ]) : null;
      if (ready) {
        try {
          const text = ` (${formatPiecePrice(result.piecePrice, locale, input.currency)} / ${this.dataset.unitLabel})`;
          if (output.textContent !== text) output.textContent = text;
          if (output.hidden) output.hidden = false;
          this.confirmedPiecePrice = priceKey ? { key: priceKey, text } : null;
        } catch {
          output.hidden = true;
          this.confirmedPiecePrice = null;
        }
      } else if (result.state === 'pending' && priceKey &&
          this.confirmedPiecePrice?.key === priceKey) {
        if (output.textContent !== this.confirmedPiecePrice.text) {
          output.textContent = this.confirmedPiecePrice.text;
        }
        if (output.hidden) output.hidden = false;
      } else {
        if (!output.hidden) output.hidden = true;
        this.confirmedPiecePrice = null;
      }
    }
  }
  customElements.define('bp-pack-content', PackContent);
}

